import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// The emergency stop.
//
// What this replaced was an environment variable, which meant halting writes on
// a customer's ERP required editing config and waiting for a deployment. That
// is the wrong tool for the one situation where minutes matter and someone is
// watching their own data change.
//
// The env var still exists and still wins. It cannot be undone from here, which
// is the point of keeping it: a bug in this screen must not be able to turn
// writes back on somewhere they were deliberately stopped.

interface Block {
  tenantId: string;
  reason: string;
  blockedBy: string;
  blockedAt: string;
}

/**
 * An obviously-fake GUID, shown as the example tenant id.
 *
 * ⚠ This placeholder used to carry a LIVE tenant's identifier, and the
 * front-end bundle is served to anyone who loads app.acutidy.com, including
 * people who never sign in. It granted nothing — a non-member gets the same
 * refusal as for a tenant that does not exist — but publishing a real customer
 * identifier in a public asset is not a habit worth keeping, and the next
 * person to paste a convenient example here might reach for a customer's.
 */
const EXAMPLE_TENANT_ID = '00000000-0000-0000-0000-000000000000';

export function TenantControlsPage() {
  const api = useApi();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<'stop' | { resume: string } | null>(null);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      const result = await api.get<{ blocks: Block[] }>('/support/write-blocks');
      setBlocks(result.blocks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load write blocks.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function stop() {
    if (!api || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.post<{ note: string }>(
        `/support/tenants/${tenantId.trim()}/write-block`,
        { blocked: true, reason }
      );
      setResult(response.note);
      setTenantId('');
      setReason('');
      setConfirming(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not stop writes.');
    } finally {
      setBusy(false);
    }
  }

  async function resume(id: string) {
    if (!api || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.post<{ note: string }>(
        `/support/tenants/${id}/write-block`,
        { blocked: false, reason }
      );
      setResult(response.note);
      setReason('');
      setConfirming(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resume writes.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorNotice message={error} />
      {result && <div className="notice success">{result}</div>}

      <div className="card">
        <h2>Stop writes for a tenant</h2>
        <div className="notice warn">
          <strong>New jobs are refused immediately.</strong> A job already running is not
          killed — Durable Functions finishes the batch it is on, and everything it wrote
          stays reversible from that tenant's job history. Reading and the grid are
          unaffected; only writing stops.
        </div>

        <label>
          <span>Tenant ID</span>
          <input
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="mono"
            placeholder={EXAMPLE_TENANT_ID}
            maxLength={64}
          />
        </label>
        <label>
          <span>Reason</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What is happening?"
            maxLength={500}
          />
          <small className="muted">Recorded in the audit log, and shown in the list below.</small>
        </label>

        <div className="form-actions">
          {confirming === 'stop' ? (
            <>
              <button type="button" onClick={() => setConfirming(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={() => void stop()} disabled={busy}>
                {busy ? 'Stopping…' : 'Yes, stop writes'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={() => setConfirming('stop')}
              disabled={!tenantId.trim() || !reason.trim()}
            >
              Stop writes
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Currently stopped</h2>
        {!blocks ? (
          <p className="muted">Loading…</p>
        ) : blocks.length === 0 ? (
          <p className="muted">No tenant has writes stopped.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Reason</th>
                <th>Since</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => (
                <tr key={block.tenantId}>
                  <td className="mono small">{block.tenantId}</td>
                  <td className="small">{block.reason}</td>
                  <td className="small">{new Date(block.blockedAt).toLocaleString()}</td>
                  <td>
                    {typeof confirming === 'object' && confirming?.resume === block.tenantId ? (
                      <div className="toolbar">
                        <button type="button" onClick={() => setConfirming(null)} disabled={busy}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="primary"
                          onClick={() => void resume(block.tenantId)}
                          disabled={busy || !reason.trim()}
                        >
                          {busy ? 'Resuming…' : 'Yes, resume'}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming({ resume: block.tenantId });
                          setReason('');
                        }}
                      >
                        Resume writes
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted small">
          If writing is still refused after resuming, this tenant is also listed in the
          <code> WRITES_DISABLED_TENANT_IDS</code> environment variable. That one needs a
          deployment to change, and deliberately cannot be cleared from here.
        </p>
      </div>
    </>
  );
}
